import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Megaphone, Send, RefreshCw, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TARGETS = [
  { value: "all", label: "Todos los usuarios" },
  { value: "premium", label: "Solo Premium activos" },
  { value: "with_credits", label: "Con créditos > 0" },
  { value: "trial_active", label: "Trial activo" },
  { value: "telegram_only", label: "Solo Telegram (no web)" },
  { value: "web_only", label: "Solo usuarios web" },
];

export default function Broadcast() {
  const [text, setText] = useState("");
  const [target, setTarget] = useState("all");
  const [testMode, setTestMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    try {
      const r = await api.get("/broadcasts");
      setHistory(r.data);
    } catch (e) {
      console.error("[Broadcast] error:", e);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  const send = async () => {
    if (!text.trim()) {
      toast.error("Escribe el mensaje primero");
      return;
    }
    if (text.length > 4000) {
      toast.error("Mensaje demasiado largo");
      return;
    }
    const targetLabel = testMode
      ? "TEST (solo admins)"
      : TARGETS.find((t) => t.value === target)?.label || target;
    if (!testMode && !window.confirm(`Vas a enviar este anuncio a "${targetLabel}". ¿Confirmas?`))
      return;
    setSubmitting(true);
    try {
      const r = await api.post("/broadcast", {
        text,
        target,
        test_mode: testMode,
      });
      toast.success(`Anuncio enviándose a ${r.data.total} destinatarios`);
      setText("");
      setTestMode(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando anuncio");
    } finally {
      setSubmitting(false);
    }
  };

  const Stat = ({ label, value, color = "text-zinc-100" }) => (
    <div className="border border-zinc-800 bg-zinc-900/50 rounded-sm px-4 py-3">
      <div className="overline text-zinc-500">{label}</div>
      <div className={`font-display font-black text-2xl tracking-tight mt-1 ${color}`}>
        {value}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 max-w-[1100px]" data-testid="broadcast-page">
      <div>
        <div className="overline mb-2">Comunicación</div>
        <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight">
          Anuncios masivos
        </h1>
        <p className="text-sm text-zinc-500 mt-2">
          Envía un mensaje al instante a tus usuarios. Llega vía Telegram y queda
          registrado en la web.
        </p>
      </div>

      {/* Composer */}
      <div className="border border-zinc-800 bg-zinc-900/50 rounded-md p-5 space-y-4">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`Escribe tu anuncio aquí...

Puedes usar Markdown:
*negrita* _cursiva_ \`código\`

Ejemplo:
🎉 *¡Promoción!* Esta semana 2x1 en créditos. Usa el código FELIZ24 al comprar.`}
          rows={10}
          maxLength={4000}
          data-testid="broadcast-text"
          className="bg-zinc-950 border-zinc-800 rounded-sm text-sm font-mono"
        />
        <div className="flex justify-between items-center text-xs">
          <div className="text-zinc-500">
            {text.length}/4000 caracteres ·{" "}
            <span className="text-zinc-400">
              Soporta *negrita*, _cursiva_, `código`, [enlaces](https://...)
            </span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
          <div className="flex-1">
            <div className="overline mb-1.5">Destinatarios</div>
            <Select value={target} onValueChange={setTarget} disabled={testMode}>
              <SelectTrigger
                className="bg-zinc-950 border-zinc-800 rounded-sm"
                data-testid="broadcast-target"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 rounded-sm">
                {TARGETS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label
            className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none"
            data-testid="broadcast-test-mode-label"
          >
            <input
              type="checkbox"
              checked={testMode}
              onChange={(e) => setTestMode(e.target.checked)}
              data-testid="broadcast-test-mode"
              className="w-4 h-4 rounded-sm bg-zinc-950 border-zinc-700"
            />
            <span>
              Modo prueba <span className="text-zinc-600">(solo a ti)</span>
            </span>
          </label>
          <Button
            onClick={send}
            disabled={submitting || !text.trim()}
            data-testid="broadcast-send-btn"
            className="bg-[#2AABEE] hover:bg-[#229ED9] text-white font-bold rounded-sm h-10 px-6"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Send size={14} className="mr-2" />
            )}
            {testMode ? "Enviar prueba" : "Enviar a todos"}
          </Button>
        </div>
      </div>

      {/* History */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-black text-xl tracking-tight flex items-center gap-2">
            <Megaphone size={18} className="text-[#2AABEE]" />
            Historial
          </h2>
          <Button
            onClick={load}
            variant="ghost"
            size="sm"
            data-testid="broadcast-refresh"
            className="text-zinc-500 hover:text-zinc-100"
          >
            <RefreshCw size={14} />
          </Button>
        </div>

        {history.length === 0 && (
          <div className="border border-dashed border-zinc-800 rounded-md p-10 text-center text-sm text-zinc-600">
            Aún no has enviado ningún anuncio.
          </div>
        )}

        <div className="space-y-3">
          {history.map((b) => {
            const isDone = b.status === "done";
            const isFailed = b.status?.startsWith("failed");
            const successRate =
              b.total > 0 ? Math.round((b.sent / b.total) * 100) : 0;
            return (
              <div
                key={b.id}
                className="border border-zinc-800 bg-zinc-900/50 rounded-md p-4"
                data-testid={`broadcast-row-${b.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-mono text-zinc-500 mb-1">
                      {new Date(b.created_at).toLocaleString("es-MX")} · {b.target?.toUpperCase()}
                      {b.test_mode ? " · TEST" : ""}
                    </div>
                    <div className="text-sm whitespace-pre-wrap line-clamp-3 text-zinc-200">
                      {b.text}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {isDone && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold px-2 py-1 rounded-sm bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                        <CheckCircle2 size={11} />
                        ENTREGADO
                      </span>
                    )}
                    {!isDone && !isFailed && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold px-2 py-1 rounded-sm bg-amber-500/10 text-amber-400 border border-amber-500/30">
                        <Loader2 size={11} className="animate-spin" />
                        ENVIANDO…
                      </span>
                    )}
                    {isFailed && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold px-2 py-1 rounded-sm bg-red-500/10 text-red-400 border border-red-500/30">
                        <AlertCircle size={11} />
                        FALLÓ
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div className="text-zinc-500">
                    Enviados: <span className="text-emerald-400 font-mono font-bold">{b.sent || 0}</span>
                    <span className="mx-1.5 text-zinc-700">·</span>
                    Fallidos: <span className="text-red-400 font-mono font-bold">{b.failed || 0}</span>
                    <span className="mx-1.5 text-zinc-700">·</span>
                    Total: <span className="text-zinc-300 font-mono">{b.total}</span>
                  </div>
                  <div className="text-zinc-500 font-mono">
                    {successRate}%
                  </div>
                </div>
                <div className="mt-2 h-1 bg-black/40 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 transition-all duration-300"
                    style={{ width: `${successRate}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
